import { ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Menu } from 'lucide-react';
import { SidebarNav } from './SidebarNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { NAV_GROUPS } from './nav-config';
import { BrandBadge } from './BrandBadge';

interface AdminLayoutProps {
  children: ReactNode;
}

/** Best label for the current route — used as the mobile top-bar title. */
function useCurrentSection(): string {
  const { pathname } = useLocation();
  const items = NAV_GROUPS.flatMap((g) => g.items);
  // Longest matching prefix wins so /meetings/:id resolves to "Meetings".
  const match = items
    .filter((i) => (i.to === '/' ? pathname === '/' : pathname.startsWith(i.to)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.label ?? 'Admin';
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const section = useCurrentSection();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop rail — fixed, always visible from lg up. */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-sidebar-border lg:block">
        <SidebarNav />
      </aside>

      {/* Mobile drawer */}
      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in lg:hidden" />
          <Dialog.Content
            className="fixed inset-y-0 left-0 z-50 w-72 border-r border-sidebar-border shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-left lg:hidden"
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <SidebarNav onNavigate={() => setDrawerOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Content column */}
      <div className="flex min-h-screen flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-sm font-semibold tracking-tight">{section}</span>
          <div className="flex-1" />
          <BrandBadge />
          <ThemeToggle />
        </header>

        <main className="flex-1 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
